import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowDownUp, Info, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ExchangePage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [fromCurrency, setFromCurrency] = useState('');
  const [toCurrency, setToCurrency] = useState('');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchCurrencies();
    fetchHistory();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/exchange/currencies`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCurrencies(data.currencies || []);
      
      // Set default currencies if available
      if (data.currencies && data.currencies.length > 0) {
        setFromCurrency(data.currencies.find(c => c.currency_code === 'ZAR')?.currency_code || data.currencies[0].currency_code);
        setToCurrency(data.currencies.find(c => c.currency_code === 'BTC')?.currency_code || data.currencies[1]?.currency_code);
      }
    } catch (error) {
      toast.error('Failed to load currencies');
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/exchange/history?limit=10`, {
        credentials: 'include'
      });
      const data = await response.json();
      setHistory(data.exchanges || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const getQuote = async () => {
    if (!fromCurrency || !toCurrency || !amount || parseFloat(amount) <= 0) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setQuote(null);

    try {
      const response = await fetch(`${API_URL}/api/user/exchange/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          from_currency: fromCurrency,
          to_currency: toCurrency,
          amount: parseFloat(amount)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to get quote');
      }

      setQuote(data);
    } catch (error) {
      toast.error(error.message || 'Failed to get quote');
    } finally {
      setLoading(false);
    }
  };

  const executeExchange = async () => {
    if (!quote) return;

    setExecuting(true);

    try {
      const response = await fetch(`${API_URL}/api/user/exchange/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          from_currency: fromCurrency,
          to_currency: toCurrency,
          amount: parseFloat(amount)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Exchange failed');
      }

      toast.success(`Successfully exchanged ${amount} ${fromCurrency} for ${data.to_amount.toFixed(8)} ${toCurrency}`);
      
      // Reset form
      setAmount('');
      setQuote(null);
      
      // Refresh history
      fetchHistory();
      
      // Reload page to update wallet balances
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error(error.message || 'Exchange failed');
    } finally {
      setExecuting(false);
    }
  };

  const swapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    setQuote(null);
  };

  const getCurrencyIcon = (code) => {
    const currency = currencies.find(c => c.currency_code === code);
    return currency?.icon || '💱';
  };

  return (
    <FocusedLayout title="Exchange" description="Swap between currencies instantly">
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* Exchange Card */}
        <Card className="border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Currency Exchange
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* From Currency */}
            <div className="space-y-2">
              <Label>From</Label>
              <div className="flex gap-2">
                <select
                  value={fromCurrency}
                  onChange={(e) => { setFromCurrency(e.target.value); setQuote(null); }}
                  className="flex-1 border rounded-md p-2 text-lg"
                >
                  {currencies.map(c => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {getCurrencyIcon(c.currency_code)} {c.currency_code} - {c.name}
                    </option>
                  ))}
                </select>
                <Input
                  type="number"
                  placeholder="Amount"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setQuote(null); }}
                  className="w-40 text-lg"
                  step="any"
                />
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="icon"
                onClick={swapCurrencies}
                className="rounded-full"
              >
                <ArrowDownUp className="h-4 w-4" />
              </Button>
            </div>

            {/* To Currency */}
            <div className="space-y-2">
              <Label>To</Label>
              <select
                value={toCurrency}
                onChange={(e) => { setToCurrency(e.target.value); setQuote(null); }}
                className="w-full border rounded-md p-2 text-lg"
              >
                {currencies.map(c => (
                  <option key={c.currency_code} value={c.currency_code}>
                    {getCurrencyIcon(c.currency_code)} {c.currency_code} - {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Get Quote Button */}
            <Button
              onClick={getQuote}
              disabled={loading || !amount || parseFloat(amount) <= 0}
              className="w-full"
              size="lg"
            >
              {loading ? 'Getting Quote...' : 'Get Quote'}
            </Button>

            {/* Quote Display */}
            {quote && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 rounded-lg p-4 space-y-3 border-2 border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">You'll receive</span>
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {quote.to_amount.toFixed(8)} {quote.to_currency}
                  </span>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exchange Rate</span>
                    <span className="font-medium">1 {quote.from_currency} = {quote.exchange_rate.toFixed(8)} {quote.to_currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Route</span>
                    <span className="font-mono text-xs">{quote.route}</span>
                  </div>
                  <div className="flex justify-between text-red-600 dark:text-red-400">
                    <span>Fees (Trading + Spread)</span>
                    <span>{quote.fees.total_fees.toFixed(8)} {quote.to_currency}</span>
                  </div>
                </div>

                <Button
                  onClick={executeExchange}
                  disabled={executing}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  size="lg"
                >
                  {executing ? 'Executing...' : 'Confirm Exchange'}
                </Button>

                <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                  <Clock className="h-3 w-3" />
                  Quote valid for 60 seconds
                </p>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium text-foreground">How it works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Select currencies and amount</li>
                  <li>Get instant quote with fees</li>
                  <li>Confirm to complete exchange</li>
                  <li>Funds credited to your wallet instantly</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Exchanges */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Exchanges</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory ? 'Hide' : 'Show'}
                </Button>
              </div>
            </CardHeader>
            {showHistory && (
              <CardContent>
                <div className="space-y-2">
                  {history.map((exchange, idx) => (
                    <div
                      key={exchange.transaction_id || idx}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="font-medium">
                            {exchange.from_amount} {exchange.from_currency} → {exchange.to_amount_after_fees?.toFixed(6)} {exchange.to_currency}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(exchange.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-muted-foreground">Rate: {exchange.exchange_rate?.toFixed(8)}</p>
                        <p className="text-xs text-red-500">Fee: {exchange.total_fees?.toFixed(6)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

      </div>
    </FocusedLayout>
  );
};
